import React, { useState, useCallback, useEffect } from "react";
import { FullscreenIcon } from "@/components/ui/icon";
import { ProgressSlider } from "@/components/common/progress-slider";
import { cn } from "@/utils/cn";
import VolumeControl from "@/components/creation-modal-video-play/volume-control";
import IconButton from "@/components/creation-modal-video-play/icon-button";
import PlayingButton from "@/components/creation-modal-video-play/playing-button";
import PlayButton from "@/components/creation-modal-video-play/play-button";
import LoadingButton from "@/components/creation-modal-video-play/loading-button";

interface Props {
  videoRef: React.RefObject<HTMLVideoElement>;
  className?: string;
  onLoad?: (load: boolean) => void;
  onError?: (error: string) => void;
}

/**
 * 参考 src/components/modal-video-play/video-controller.tsx 的实现，
 * 做轻量复用（直接引用现成的按钮与音量组件）。
 */
const AgentVideoController: React.FC<Props> = ({
  videoRef,
  className = "",
  onLoad,
  onError,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [videoVolume, setVideoVolume] = useState(100);
  const [videoMuted, setVideoMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const togglePlay = useCallback(async () => {
    if (!videoRef.current || hasError) return;
    try {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        await videoRef.current.play();
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Play/Pause failed";
      setHasError(true);
      onError?.(msg);
    }
  }, [isPlaying, videoRef, hasError, onError]);

  const toggleFullscreen = useCallback(async () => {
    if (!videoRef.current) return;
    try {
      if (document.fullscreenElement) {
        if (document.exitFullscreen) await document.exitFullscreen();
      } else {
        if (videoRef.current.requestFullscreen) {
          await videoRef.current.requestFullscreen();
        }
      }
    } catch (error) {
      onError?.("Fullscreen toggle failed");
    }
  }, [videoRef, onError]);

  const seekVideo = useCallback(
    (seconds: number) => {
      if (!videoRef.current || hasError) return;
      const total = duration;
      const next = Math.max(0, Math.min(total, currentTime + seconds));
      videoRef.current.currentTime = next;
      setCurrentTime(next);
      if (total > 0) setProgress((next / total) * 100);
    },
    [currentTime, duration, videoRef, hasError]
  );

  const handleProgressChange = useCallback(
    (value: number) => {
      if (!videoRef.current || duration <= 0) return;
      const next = (value / 100) * duration;
      videoRef.current.currentTime = next;
      setCurrentTime(next);
      setProgress(value);
    },
    [duration, videoRef]
  );

  const handleDragStart = useCallback(() => setIsDragging(true), []);
  const handleDragEnd = useCallback(() => setIsDragging(false), []);

  const formatTime = useCallback((time: number) => {
    if (!isFinite(time)) return "00:00";
    const m = Math.floor(time / 60)
      .toString()
      .padStart(2, "0");
    const s = Math.floor(time % 60)
      .toString()
      .padStart(2, "0");
    return `${m}:${s}`;
  }, []);

  // 监听视频事件
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const handleTimeUpdate = () => {
      if (!isDragging) {
        const cur = v.currentTime;
        const total = v.duration || 0;
        setCurrentTime(cur);
        setDuration(total);
        if (total > 0) setProgress((cur / total) * 100);
      }
    };
    const handlePlay = () => {
      setIsPlaying(true);
      setHasError(false);
    };
    const handlePause = () => setIsPlaying(false);
    const handleLoadedData = () => {
      setDuration(v.duration || 0);
      setIsLoading(false);
      setHasError(false);
    };
    const handleLoadStart = () => setIsLoading(true);
    const handleError = () => {
      setIsLoading(false);
      setHasError(true);
      setIsPlaying(false);
      onError?.("Failed to load video");
    };
    const handleWaiting = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);
    const handleVolumeChange = () => {
      setVideoVolume(Math.round(v.volume * 100));
      setVideoMuted(v.muted);
    };

    v.addEventListener("timeupdate", handleTimeUpdate);
    v.addEventListener("play", handlePlay);
    v.addEventListener("pause", handlePause);
    v.addEventListener("loadeddata", handleLoadedData);
    v.addEventListener("loadstart", handleLoadStart);
    v.addEventListener("error", handleError);
    v.addEventListener("waiting", handleWaiting);
    v.addEventListener("canplay", handleCanPlay);
    v.addEventListener("volumechange", handleVolumeChange);

    return () => {
      v.removeEventListener("timeupdate", handleTimeUpdate);
      v.removeEventListener("play", handlePlay);
      v.removeEventListener("pause", handlePause);
      v.removeEventListener("loadeddata", handleLoadedData);
      v.removeEventListener("loadstart", handleLoadStart);
      v.removeEventListener("error", handleError);
      v.removeEventListener("waiting", handleWaiting);
      v.removeEventListener("canplay", handleCanPlay);
      v.removeEventListener("volumechange", handleVolumeChange);
    };
  }, [videoRef, isDragging, onError]);

  useEffect(() => onLoad?.(isLoading), [isLoading, onLoad]);

  return (
    <div className={cn("flex items-center gap-2 p-2", className)}>
      {/* 播放/暂停 */}
      {isPlaying ? (
        isLoading ? (
          <LoadingButton onClick={togglePlay} disabled={hasError} />
        ) : (
          <PlayingButton onClick={togglePlay} disabled={hasError} />
        )
      ) : (
        <PlayButton onClick={togglePlay} disabled={hasError} />
      )}

      <div className="flex flex-1 items-center gap-2 p-1 h-10 rounded-sm bg-[rgba(17,24,39,0.6)] backdrop-blur-xl">
        {/* 音量控制 */}
        <VolumeControl
          initialVolume={videoVolume}
          initialMuted={videoMuted}
          onVolumeChange={(volume) => {
            if (videoRef.current) {
              setVideoVolume(volume);
              videoRef.current.volume = volume / 100;
            }
          }}
          onMutedChange={(muted) => {
            if (videoRef.current) {
              setVideoMuted(muted);
              videoRef.current.muted = muted;
            }
          }}
        />

        {/* 当前时间 */}
        <span className="text-white text-xs font-medium w-auto">
          {formatTime(currentTime)}
        </span>

        {/* 进度条 */}
        <div className="flex-1">
          <ProgressSlider
            value={progress}
            max={100}
            trackColor="rgba(255,255,255,0.5)"
            progressColor="rgb(255,255,255)"
            thumbColor="rgb(255,255,255)"
            thumbBorderColor="rgb(255,255,255)"
            onChange={handleProgressChange}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            disabled={hasError}
            className="w-full"
          />
        </div>

        {/* 总时长 */}
        <span className="text-white text-xs font-medium w-auto">
          {formatTime(duration)}
        </span>

        {/* 全屏 */}
        <IconButton onClick={toggleFullscreen} disabled={hasError}>
          <FullscreenIcon className="w-5 h-5 text-white" />
        </IconButton>

        {/* 快退/快进 15s（可选按钮组，如果需要可以解注释） */}
        {/* <IconButton onClick={() => seekVideo(-15)} disabled={hasError}>
          <span className="text-white text-xs">-15s</span>
        </IconButton>
        <IconButton onClick={() => seekVideo(15)} disabled={hasError}>
          <span className="text-white text-xs">+15s</span>
        </IconButton> */}
      </div>
    </div>
  );
};

export default AgentVideoController;